import type { Board, GeometryElement, Point } from 'jsxgraph';
import { pixelsToCoords } from '../elements/label.creator';
import type { ElementCreatorContext } from '../elements/core/board.types';
import type { SnappingConfig } from '../elements/point/snapping.model';

/** Options for binding a grab handle to a point */
export interface BindGrabHandleOptions {
  ctx: ElementCreatorContext;
  point: Point;
  color: string;
  size?: number;
  onDrag?: (deltaX: number, deltaY: number) => void;
  /** Snapping configuration to apply during drag */
  snapping?: SnappingConfig;
}

/** Result of binding a grab handle */
export interface BoundGrabHandle {
  element: GeometryElement;
  destroy: () => void;
}

/**
 * Bind a teardrop-shaped grab handle to a point.
 * The grab handle is positioned above the point and translates drag interactions to the point.
 *
 * @param options - Grab handle binding options
 * @returns The bound grab handle with element and destroy method
 */
export function bindGrabHandle(options: BindGrabHandleOptions): BoundGrabHandle {
  const { ctx, point, color, size = 60, onDrag, snapping } = options;
  const { board, container } = ctx;

  const typedPoint = point as unknown as { X: () => number; Y: () => number };

  // Offset to position handle above the point (handle tip points down)
  const offsetY = -size * 1.05;

  // Create the grab handle as a JSXGraph text element with HTML content
  const grabHandle = board.create(
    'text',
    [
      () => typedPoint.X(),
      () => typedPoint.Y() + pixelsToCoords(board, container, offsetY, 'y'),
      () => createGrabHandleSvg(color, size),
    ],
    {
      anchorX: 'middle',
      anchorY: 'bottom',
      parse: false,
      useMathjax: false,
      useMathJax: false,
      cssClass: 'jsx-grab-handle',
      highlight: false,
      fixed: false,
    },
  ) as GeometryElement;

  // Set up drag handling on the grab handle
  setupGrabHandleDrag(board, container, grabHandle, point, onDrag, snapping);

  return {
    element: grabHandle,
    destroy: () => {
      board.removeObject(grabHandle);
    },
  };
}

/**
 * Create the teardrop SVG markup for the grab handle.
 */
function createGrabHandleSvg(color: string, size: number): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 32 32" style="overflow: visible; cursor: grab; touch-action: none; filter: drop-shadow(0 0 3px rgba(0,0,0,0.25));">
    <path
      d="M16 30 C22 30 26 25 26 19 C26 13 16 2 16 2 C16 2 6 13 6 19 C6 25 10 30 16 30 Z"
      fill="white"
    />
    <circle cx="16" cy="19" r="6" fill="${color}" />
  </svg>`;
}

/**
 * Apply grid snapping to a coordinate value.
 */
function applyGridSnapping(value: number, snapSize: number | undefined): number {
  if (snapSize === undefined || snapSize <= 0) {
    return value;
  }
  return Math.round(value / snapSize) * snapSize;
}

/**
 * Set up drag handling for the grab handle.
 * Translates mouse/touch drag movements to the point position.
 */
function setupGrabHandleDrag(
  board: Board,
  container: HTMLElement,
  grabHandle: GeometryElement,
  point: Point,
  onDrag?: (deltaX: number, deltaY: number) => void,
  snapping?: SnappingConfig,
): void {
  const typedPoint = point as unknown as {
    X: () => number;
    Y: () => number;
    setPositionDirectly: (type: number, coords: [number, number]) => void;
  };

  let isDragging = false;
  let lastClientX = 0;
  let lastClientY = 0;
  // Track the target position separately to handle snapping correctly
  // This accumulates deltas without snapping, then we apply snapping for display
  let targetX = 0;
  let targetY = 0;

  const handleElement = (grabHandle as unknown as { rendNode: HTMLElement }).rendNode;
  if (!handleElement) {
    return;
  }

  const onMouseDown = (e: MouseEvent) => {
    if (e.button !== 0) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    isDragging = true;
    lastClientX = e.clientX;
    lastClientY = e.clientY;
    // Initialize target position from current point position
    targetX = typedPoint.X();
    targetY = typedPoint.Y();
    handleElement.style.cursor = 'grabbing';
  };

  const onMouseMove = (e: MouseEvent) => {
    if (!isDragging) {
      return;
    }
    e.preventDefault();

    const deltaClientX = e.clientX - lastClientX;
    const deltaClientY = e.clientY - lastClientY;
    lastClientX = e.clientX;
    lastClientY = e.clientY;

    // Convert pixel deltas to coordinate deltas
    const deltaX = pixelsToCoords(board, container, deltaClientX, 'x');
    const deltaY = -pixelsToCoords(board, container, deltaClientY, 'y');

    // Accumulate deltas on the unsnapped target position
    targetX += deltaX;
    targetY += deltaY;

    // Apply grid snapping if configured
    let newX = targetX;
    let newY = targetY;
    if (snapping?.grid?.enabled) {
      newX = applyGridSnapping(targetX, snapping.grid.sizeX);
      newY = applyGridSnapping(targetY, snapping.grid.sizeY);
    }

    typedPoint.setPositionDirectly(1, [newX, newY]);
    board.update();

    onDrag?.(deltaX, deltaY);
  };

  const onMouseUp = () => {
    if (!isDragging) {
      return;
    }
    isDragging = false;
    handleElement.style.cursor = 'grab';
  };

  const onTouchStart = (e: TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isDragging = true;
    lastClientX = e.touches[0].clientX;
    lastClientY = e.touches[0].clientY;
    // Initialize target position from current point position
    targetX = typedPoint.X();
    targetY = typedPoint.Y();
  };

  const onTouchMove = (e: TouchEvent) => {
    if (!isDragging) {
      return;
    }
    e.preventDefault();

    const touch = e.touches[0];
    const deltaClientX = touch.clientX - lastClientX;
    const deltaClientY = touch.clientY - lastClientY;
    lastClientX = touch.clientX;
    lastClientY = touch.clientY;

    const deltaX = pixelsToCoords(board, container, deltaClientX, 'x');
    const deltaY = -pixelsToCoords(board, container, deltaClientY, 'y');

    // Accumulate deltas on the unsnapped target position
    targetX += deltaX;
    targetY += deltaY;

    // Apply grid snapping if configured
    let newX = targetX;
    let newY = targetY;
    if (snapping?.grid?.enabled) {
      newX = applyGridSnapping(targetX, snapping.grid.sizeX);
      newY = applyGridSnapping(targetY, snapping.grid.sizeY);
    }

    typedPoint.setPositionDirectly(1, [newX, newY]);
    board.update();

    onDrag?.(deltaX, deltaY);
  };

  const onTouchEnd = () => {
    isDragging = false;
  };

  handleElement.addEventListener('mousedown', onMouseDown);
  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
  handleElement.addEventListener('touchstart', onTouchStart, { passive: false });
  document.addEventListener('touchmove', onTouchMove, { passive: false });
  document.addEventListener('touchend', onTouchEnd);
}
